/**
 * A project's repositories by name (AIOE 0.34.0, workbench Appendix G / D245): the name an owner can change in place,
 * and removing a repository behind a confirmation that says what removing it cancels.
 */

import type { RootOpenCounts } from '@aioe/shared';
import { Pencil, TrashBin } from '@gravity-ui/icons';
import { Tooltip } from '@heroui/react';
import { useState } from 'react';
import { ApiError, problemText, routes } from '../api/client.ts';
import { Button, ConfirmDialog, ErrorNote, inputClass } from './ui.tsx';

/** The repository's name; for an owner a pencil turns it into a field. Enter or Save keeps it, Escape or Cancel leaves it. */
export function RootName({ projectId, rootId, name, owner, onChange }: { projectId: string; rootId: string; name: string; owner: boolean; onChange: () => void }) {
  const [draft, setDraft] = useState<string>();
  const [error, setError] = useState<string>();
  const save = async () => {
    const next = (draft ?? '').trim();
    if (!next || next === name) return setDraft(undefined);
    try {
      setError(undefined);
      await routes.renameProjectRoot(projectId, rootId, next);
      setDraft(undefined);
      onChange();
    } catch (e) {
      setError(problemText(e));
    }
  };
  if (draft === undefined) {
    return (
      <div className="flex items-center gap-1 font-medium">
        <span className="truncate">{name}</span>
        {owner && (
          <Tooltip delay={300}>
            <Tooltip.Trigger>
              <Button isIconOnly size="sm" variant="ghost" aria-label={`Rename ${name}`} onPress={() => setDraft(name)}>
                <Pencil />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>Rename</Tooltip.Content>
          </Tooltip>
        )}
      </div>
    );
  }
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <input
          autoFocus
          className={`${inputClass} max-w-64`}
          aria-label="Repository name"
          maxLength={100}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setDraft(undefined);
          }}
        />
        <Button size="sm" onPress={() => void save()}>
          Save
        </Button>
        <Button size="sm" variant="tertiary" onPress={() => setDraft(undefined)}>
          Cancel
        </Button>
      </div>
      <ErrorNote error={error} />
    </div>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** What removing would cancel, as the platform counted it. */
function Counts({ open }: { open: RootOpenCounts }) {
  const lines = [
    open.tasks ? `${plural(open.tasks, 'open task')}${open.claims ? `, ${open.claims} of them being worked on now` : ''}` : '',
    open.parts ? `${plural(open.parts, 'part')} of project tasks` : '',
    open.plans ? `${plural(open.plans, 'plan')} not yet finished` : '',
  ].filter(Boolean);
  return (
    <ul className="list-disc pl-5">
      {lines.map((l) => (
        <li key={l}>{l}</li>
      ))}
    </ul>
  );
}

/** Remove: first a plain question, then, when the platform finds work open, the counts and a second, firmer confirmation. */
export function RemoveRoot({ projectId, rootId, name, onChange }: { projectId: string; rootId: string; name: string; onChange: () => void }) {
  const [step, setStep] = useState<'closed' | 'ask' | 'work'>('closed');
  const [work, setWork] = useState<{ open: RootOpenCounts; message?: string }>();
  const [error, setError] = useState<string>();
  const close = () => {
    setStep('closed');
    setWork(undefined);
  };
  const remove = async (confirm: boolean) => {
    try {
      setError(undefined);
      await routes.removeProjectRoot(projectId, rootId, confirm);
      close();
      onChange();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === 'root_has_work') {
        const b = e.body as { open: RootOpenCounts; message?: string };
        setWork({ open: b.open, message: b.message });
        setStep('work');
        return;
      }
      close();
      setError(problemText(e));
    }
  };
  return (
    <div className="grid gap-1">
      <Tooltip delay={300}>
        <Tooltip.Trigger>
          <Button isIconOnly size="sm" variant="ghost" aria-label={`Remove ${name} from the project`} onPress={() => setStep('ask')}>
            <TrashBin />
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content>Remove from the project</Tooltip.Content>
      </Tooltip>
      <ErrorNote error={error} />
      <ConfirmDialog isOpen={step === 'ask'} title={`Remove ${name} from the project?`} confirmLabel="Remove" danger onConfirm={() => remove(false)} onCancel={close}>
        <p>Its board and map stay readable for 30 days, then they are dropped. The repository itself is not touched.</p>
      </ConfirmDialog>
      <ConfirmDialog isOpen={step === 'work'} title={`${name} still has work open`} confirmLabel="Cancel the work and remove" danger onConfirm={() => remove(true)} onCancel={close}>
        {work && (
          <>
            <p>{work.message}</p>
            <Counts open={work.open} />
            <p>The open tasks are cancelled, the machines working on them are told to stop, and its plans are abandoned.</p>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
